import Head from 'next/head';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import KakaoIcon from '@/components/atom/icon/Kakao';
import Link from '@/components/atom/icon/Link';
import { mobileLogger } from '@/utils/logger';
import { useToast } from 'doctornow-design-system';

import SelectModal, { SelectModalListItem } from '..';

export const KAKAO_SHARE_TYPE = {
  QNA: 'QNA',
  MAGAZINE: 'MAGAZINE',
  BOARD_POST: 'BOARD_POST',
  NON_BENEFIT_MAP: 'NON_BENEFIT_MAP',
};

export type KAKAO_SHARE_TYPE = (typeof KAKAO_SHARE_TYPE)[keyof typeof KAKAO_SHARE_TYPE];

interface KakaoShareProps {
  id: string;
  subId?: string;
  title: string;
  content: string;
  imageUrl?: string;
}

interface CopyLinkProps {
  id: string;
  copyElementId: string;
  copyLinkUrl: string;
}

interface ShareSelectModalProps {
  isVisible: boolean;
  onClose: () => void;
  type: KAKAO_SHARE_TYPE;
  pageName: string;
  kakaoShare?: KakaoShareProps;
  copyLink?: CopyLinkProps;
}

/**
 * @param isVisible boolean
 * @param onClose () => void
 * @param type KAKAO_SHARE_TYPE
 * @param pageName string
 * @param kakaoShare [optional] KakaoShareProps
 * @param copyLink? CopyLinkProps
 */

const ShareSelectModal: FunctionComponent<ShareSelectModalProps> = ({
  isVisible,
  onClose,
  type,
  pageName,
  kakaoShare,
  copyLink,
}) => {
  const { toast } = useToast();
  const [_isVisible, _setIsVisible] = useState<boolean>(false);
  const [shareItems, setShareItems] = useState<Array<SelectModalListItem>>();
  const eventNamePath = type.toLocaleLowerCase();

  // 공유하기 [카카오]
  const onShareKakao = useCallback(() => {
    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(process.env.NEXT_PUBLIC_KAKAO_JS_KEY);
    }

    if (kakaoShare) {
      mobileLogger({
        logger: {
          eventName: `${eventNamePath}_share_kakao_click`,
          pageName,
          payload: { id: kakaoShare.id },
        },
      });
      const { Kakao } = window;

      if (type === KAKAO_SHARE_TYPE.QNA) {
        Kakao.Share.sendCustom({
          templateId: 90853,
          templateArgs: {
            questionPid: kakaoShare.id,
            title: kakaoShare.title,
            content: kakaoShare.content,
          },
        });
      }

      if (type === KAKAO_SHARE_TYPE.MAGAZINE) {
        Kakao.Share.sendCustom({
          templateId: 90880,
          templateArgs: {
            magazinePid: kakaoShare.id,
            title: kakaoShare.title,
            description: kakaoShare.content,
            titleImageUrl: kakaoShare.imageUrl,
          },
        });
      }

      if (type === KAKAO_SHARE_TYPE.BOARD_POST) {
        Kakao.Share.sendCustom({
          templateId: 94245,
          templateArgs: {
            id: kakaoShare.id,
            title: kakaoShare.title,
            content: kakaoShare.content,
          },
        });
      }

      if (type === KAKAO_SHARE_TYPE.NON_BENEFIT_MAP) {
        Kakao.Share.sendCustom({
          templateId: 114723,
          templateArgs: {
            diagnosisTypeId: kakaoShare.id,
            treatmentMedicineId: kakaoShare.subId,
            title: kakaoShare.title,
            description: kakaoShare.content,
            thumbnailUrl: kakaoShare.imageUrl,
          },
        });
      }
    }
    onClose();
  }, [type, kakaoShare, eventNamePath, pageName, onClose]);

  // 공유하기 [링크복사]
  const onCopyLink = useCallback(() => {
    if (copyLink) {
      mobileLogger({
        logger: {
          eventName: `${eventNamePath}_copy_link_click`,
          pageName,
          payload: {
            id: copyLink?.id,
          },
        },
      });
      document.getElementById(copyLink?.copyElementId)?.click();
    }
    onClose();
  }, [copyLink, eventNamePath, pageName, onClose]);

  const onCompleteCopy = () => {
    toast({ text: '클립보드로 복사되었습니다.', direction: 'bottom' });
  };

  const generateSelectList = useCallback(() => {
    let selectItems: Array<SelectModalListItem> = [];
    if (copyLink) {
      selectItems = [
        {
          Icon: () => <Link hasWrapper />,
          text: '링크 복사',
          onClick: onCopyLink,
        },
        ...selectItems,
      ];
    }
    if (kakaoShare) {
      selectItems = [
        {
          Icon: () => <KakaoIcon />,
          text: '카카오톡으로 공유',
          onClick: onShareKakao,
        },
        ...selectItems,
      ];
    }
    setShareItems(selectItems);
  }, [copyLink, onShareKakao, onCopyLink, kakaoShare, setShareItems]);

  useEffect(() => {
    generateSelectList();
  }, [generateSelectList]);

  useEffect(() => {
    _setIsVisible(isVisible);
  }, [isVisible]);
  // https://sharer.kakao.com/picker/link?app_key=b921b0231b604bf1aea6460c3d26cb57&short_key=7007dc80-2f45-494f-a8fc-3ad2b18dd522
  return (
    <>
      <Head>
        <script defer src='https://developers.kakao.com/sdk/js/kakao.min.js' />
      </Head>
      {_isVisible && !!shareItems && <SelectModal modalTitle={'공유하기'} selectList={shareItems} onClose={onClose} />}
      {!!copyLink && (
        <CopyToClipboard text={copyLink.copyLinkUrl} onCopy={onCompleteCopy}>
          <span id={copyLink.copyElementId} />
        </CopyToClipboard>
      )}
    </>
  );
};

export default ShareSelectModal;
